// @flow

import type {
  AbsenceType,
  AbsenceRecordingConfigType,
  EmployeeAbsenceRecordingConfigType,
  EmployeeAbsenceRecordingGSAppType,
  DataSecuritySettingsType,
  ClientType,
  CallbackQueueType,
  CallbackType,
  CallScriptType,
  CallInteractionType,
  ReasonAndDurationType,
  QueryType,
  EmployeeShiftPatternType,
  ShiftPatternAnswersType,
  AbsenceTimeLostType,
  ShiftPatternType,
  EmployeePositionType,
  EmployeeRecordNotificationType,
  AutoIdDataType,
  CallPurposeType,
  ReferralType,
  MedicalTriageItem,
  TriageDetails,
  JobDuty,
  QuestionAnswer,
} from "@modules/recording/types";
import config from "@config";
import type { PaginationExtendedType } from "@types";
import type { State } from "@store/reducers";
import type { CallScriptManagementType } from "@modules/management/types";
import employeeSearchFields from "../constants/employeeSearchFields";
import { getTime } from "date-fns";
import type { EmployeeType } from "@modules/employee/types";
import { type Question } from "@components";
import type {
  NurseTransferViewModel,
  NurseTransferQueueViewModel,
} from "@modules/recording/reducer/types";

export * from "./triage";

export const isLoading = (state: State): boolean => (state.recording || {}).requestsInProgress > 0;

export const isNextCallbackModalVisible = (state: State): boolean =>
  (((state.recording || {}).modals || {}).nextCallback || {}).visible;

export const isIncomingCallModalVisible = (state: State): boolean =>
  (((state.recording || {}).modals || {}).incomingCall || {}).visible;

export const isNurseCallbackModalVisible = (state: State): boolean =>
  (((state.recording || {}).modals || {}).nurseCallback || {}).visible;

export const isNurseTransferModalVisible = (state: State): boolean =>
  (((state.recording || {}).modals || {}).nurseTransfer || {}).visible;

export const selectNextCallback = (state: State): ?CallbackType =>
  ((((state || {}).recording || {}).modals || {}).nextCallback || {}).callback;

export const showCloseAbsenceModal = (state: State): boolean =>
  (((state.recording || {}).modals || {}).closeAbsenceModal || {}).visible;

export const selectError = (state: State): string | null => (state.recording || {}).error;

export const selectAbsence = (state: State): ?AbsenceType => (state.recording || {}).absence;

export const selectDefaultWizardStep = (state: State): number =>
  (state.recording || {}).defaultWizardStep;

export const selectAbsenceId = (state: State): ?number =>
  (((state || {}).recording || {}).callInteraction || {}).absenceId;

export const selectCallInteraction = (state: State): ?CallInteractionType =>
  ((state || {}).recording || {}).callInteraction;

export const selectCallInteractionId = (state: State): ?number =>
  (((state || {}).recording || {}).callInteraction || {}).id;

export const selectDynamicQuestionAnswers = (state: State): Array<QuestionAnswer> =>
  state.recording.questionsAnswers.dynamic;

export const selectStandardQuestionAnswers = (state: State): Array<QuestionAnswer> =>
  state.recording.questionsAnswers.standard;

export const selectStandardQuestions = (state: State): Array<Question> =>
  (state.recording || {}).standardQuestions.reduce((acc, c) => {
    c.question.questionLabel = "standard-question";
    return [...acc, c.question];
  }, []);

export const selectDynamicQuestions = (state: State): Array<Question> =>
  (state.recording || {}).dynamicQuestions.reduce((acc, c) => {
    c.question.questionLabel = "dynamic-question";
    return [...acc, c.question];
  }, []);

export const selectReferrals = (state: State): Array<ReferralType> => {
  return (state.recording || {}).referrals.list;
};

export const selectReferralsIsValid = (state: State): boolean => {
  return (state.recording || {}).referrals.isValid;
};

export const selectCallbackQueues = (state: State): CallbackQueueType[] =>
  ((state.recording || {}).callbackQueues || {}).content || [];

export const selectCallbackScript = (state: State): ?CallScriptManagementType =>
  (state.recording || {}).callbackScript;

export const selectEmployeeSearch = (state: State): Object =>
  (state.recording || {}).employeeSearch;

export const selectEmployeeFilters = (state: State): Object =>
  (state.recording || {}).employeeSearch.filters;

export const shouldSubmitCurrentStep = (state: State): boolean =>
  (state.recording || {}).submitCurrentStep;

export const selectCurrentStepData = (state: State): any => (state.recording || {}).currentStepData;

export const selectQuery = (state: State): ?QueryType => (state.recording || {}).query;

export const getEmployeeSchema = (state: State): ?Object =>
  (state.recording || {}).schemas.employee;

export const selectAbsenceReasonAndDurationSchema = (state: State): ?Object =>
  (state.recording || {}).schemas.reasonAndDuration;

export const selectReasonAndDurationFormData = (state: State): Object =>
  (state.recording || {}).reasonAndDurationFormData;

export const isCloseable = (state: State): boolean =>
  (((state.recording || {}).callInteraction || {}).reasonAndDuration || {}).isCloseable;

export const selectRtwAt = (state: State): string =>
  (((state.recording || {}).callInteraction || {}).reasonAndDuration || {}).rtwAt;

export const selectAbsenceLandingSchema = (state: State): ?Object =>
  ((state.recording || {}).schemas || {}).absenceLanding;

export const selectNurseDeclineReasons = (state: State): ?Object =>
  (((state.recording || {}).schemas.nurseTransferOfferSchema || {}).properties || {})
    .nurseDeclineReason;

export const selectTimeLostSchema = (state: State): ?Object =>
  (state.recording || {}).schemas.timeLost;

export const getInteractionNotesSchema = (state: State): ?Object =>
  (state.recording || {}).schemas.interactionNotes;

export const selectWizardState = (state: State): Array<Object> =>
  (state.recording || {}).wizardState || [];

export const selectCallerIdentity = (state: State): string | null => {
  return (state.recording || {}).wizardState[2]
    ? (state.recording || {}).wizardState[2].callerIdentity
    : null;
};

export const getClient = (state: State): ?ClientType => (state.recording || {}).client;

export const getClientName = (state: State): string => getClient(state)?.name || "";

export const getAbsenceDetailsSubmitted = (state: State): boolean =>
  state.recording.isAbsenceDetailsSubmitted;

export const selectAbsenceStartsAt = (state: State): string =>
  ((selectCallInteraction(state) || {}).landing || {}).absenceStartsAt;

export const selectAbsenceType = (state: State): ?string =>
  ((selectCallInteraction(state) || {}).landing || {}).absenceType;

export const selectClientId = (state: State): ?number => (getClient(state) || {}).id;

export const selectInteractionType = (state: State): ?string =>
  (state.recording || {}).interactionType;

export const selectCloseAbsenceOverride = (state: State): ?boolean =>
  (state.recording || {}).closeAbsenceOverride;

// $FlowIgnore
export const selectAbsenceReasons = (state: State): Array<{ id: number, label: string }> =>
  (getClient(state) || {}).absenceReasons || [];

export const selectClientNurseTransferType = (state: State): string | null =>
  (getClient(state) || {}).nurseTransferType;

export const selectReasonAndDuration = (state: State): ?ReasonAndDurationType =>
  ((state.recording || {}).callInteraction || {}).reasonAndDuration;

export const selectEmployee = (state: State): ?EmployeeType => (state.recording || {}).employee;

export const isFirstTimeCaller = (state: State): boolean => {
  const employee = selectEmployee(state);

  return (
    !employee ||
    !employee.securityQuestion ||
    (employee.securityQuestion &&
      employee.securityQuestion.question === null &&
      employee.securityQuestion.answer === null)
  );
};

export const selectEmployeeId = (state: State): ?number => (selectEmployee(state) || {}).id;

export const selectFailedSecurityQuestionAction = (state: State): string => {
  return ((((state.settings || {}).settings || {}).serviceSettings || {}).employeeIdentity || {})
    .processForFailedSecurityQuestion;
};

export const selectDataSecuritySettings = (state: State): ?DataSecuritySettingsType =>
  (state.recording || {}).clientSettings;

export const canCreateEmployees = (state: State): boolean =>
  (selectDataSecuritySettings(state) || {}).permittedToCreateNewEmployeeRecords;

export const hasThirdPartyConsent = (state: State): boolean =>
  (selectDataSecuritySettings(state) || {}).thirdPartyConsent;

export const selectIdentityData = (state: State): Array<$Keys<typeof employeeSearchFields>> =>
  (selectDataSecuritySettings(state) || {}).identityVerificationCheck || [];

export const selectAbsenceRecordingConfig = (state: State): ?AbsenceRecordingConfigType =>
  (state.recording || {}).absenceRecordingConfig;

export const selectActiveClients = (state: State): ClientType[] => {
  return ((state.recording || {}).absenceRecordingConfig || {}).activeClients || [];
};

// Script selectors

export const commonScriptSelector = (key: string) => (config: ?Object): CallScriptType => {
  const callScripts = (config || {}).callScripts || [];
  const script = callScripts.find(el => el.keyName === key);

  return script || { instructionText: "", scriptText: "", keyName: "" };
};

/**
 * Common script selector
 * @param  {string} key) The script key.
 * @return {Script}
 */
export const selectScript = (key: string) => (state: State): CallScriptType => {
  const config = selectAbsenceRecordingConfig(state);
  return commonScriptSelector(key)(config);
};

/**
 * Selects script text.
 * @param  {string} key The script key.
 * @return {string}
 */
export const selectScriptText = (key: string) => (state: State) =>
  selectScript(key)(state).scriptText;

/**
 * Selects the script instruction
 * @param  {string} key The script key.
 * @return {string}
 */
export const selectInstructionText = (key: string) => (state: State) =>
  selectScript(key)(state).instructionText;

/**
 * ClientType script selector.
 * @param  {string} key) The script key.
 * @return {Script}
 */
export const selectClientScript = (key: string) => (state: State): CallScriptType => {
  const client = getClient(state);
  return commonScriptSelector(key)(client);
};

export const selectEmployeeAbsenceRecording = (state: State): EmployeeAbsenceRecordingConfigType =>
  (state.recording || {}).employeeAbsenceRecording;

export const selectEmployeeAbsenceRecordingGSApp = (
  state: State,
): EmployeeAbsenceRecordingGSAppType => state.recording.employeeAbsenceRecording.goodShapeApp;

export const selectEmployeeAbsences = (state: State): Array<AbsenceType> =>
  (selectEmployeeAbsenceRecording(state) || {}).absences || [];

export const selectEmployeeOpenAbsence = (state: State): ?AbsenceType =>
  selectEmployeeAbsences(state).find(el => el.isActive) || null;

export const hasOpenAbsence = (state: State): boolean => selectEmployeeOpenAbsence(state) !== null;

export const selectLastClosedAbsence = (state: State): ?AbsenceType => {
  const employeeAbsences = selectEmployeeAbsences(state);
  const closedEmployeeAbsences = employeeAbsences.filter(absence => !absence.isActive);

  if (closedEmployeeAbsences.length === 0) {
    return null;
  }

  return closedEmployeeAbsences.sort((a, b) => getTime(b.closeAt) - getTime(a.closeAt))[0];
};

export const selectReopenableAbsences = (state: State): Array<AbsenceType> =>
  selectEmployeeAbsences(state).filter(el => el.isReopenable);

export const canOpenAbsence = (state: State): boolean | null =>
  (selectEmployeeAbsenceRecording(state) || {}).canOpenAbsence;

export const canCloseAbsence = (state: State): ?boolean =>
  (selectEmployeeAbsenceRecording(state) || {}).canCloseAbsence;

export const canReopenAbsence = (state: State): boolean | null =>
  (selectEmployeeAbsenceRecording(state) || {}).canReopenAbsence;

export const canUpdateAbsence = (state: State): boolean | null =>
  (selectEmployeeAbsenceRecording(state) || {}).canUpdateAbsence;

export const getEmployeePositions = (state: State): Array<EmployeePositionType> =>
  ((state.recording || {}).employeePositions.content || []).filter(
    (ep: EmployeePositionType) => ep.isActive,
  );

export const getEmployeeRoleAssignedShiftPatterns = (
  state: State,
): Array<EmployeeShiftPatternType> => {
  return getEmployeePositions(state)
    .filter(p => p.shiftPattern)
    .map(p => ({
      position: p.jobTitle,
      positionId: p.id,
      organisationalUnit: p.organisationalUnit.name,
      assignedPattern: (p.shiftPattern || {}).name || "-",
      assignedPatternId: (p.shiftPattern || {}).id,
    }));
};

export const getEmployeeShiftPatterns = (state: State): Array<ShiftPatternType> =>
  getEmployeePositions(state).reduce(
    (acc, p) => (p.shiftPattern ? [...acc, p.shiftPattern] : acc),
    [],
  );

export const getClientShiftPatterns = (state: State): Array<ShiftPatternType> =>
  (state.recording || {}).clientShiftPatterns.content;

export const getShiftPatternAnswers = (state: State): ShiftPatternAnswersType =>
  (state.recording || {}).shiftPatternAnswers;

export const getAbsenceRecordedTimeLost = (state: State): ?Array<AbsenceTimeLostType> =>
  ((state.recording || {}).selectedAbsence || {}).timeLost;

export const getAbsenceShiftPattern = (state: State): Array<AbsenceTimeLostType> =>
  (state.recording || {}).shiftPatternAnswers.timeLost;

export const getUpdateShiftPatternTimeLost = (state: State): ?Array<AbsenceTimeLostType> =>
  (state.recording || {}).updateTimeLost;

export const getAbsenceTotalTimeLostInSeconds = (state: State): number => {
  return ((state.recording || {}).shiftPatternAnswers.timeLost || []).reduce(
    (acc: number, t: AbsenceTimeLostType) => acc + t.timeLostInSeconds,
    0,
  );
};

export const getAbsenceTotalWorkingDaysLost = (state: State): number => {
  return (
    (getAbsenceShiftPattern(state) || []).reduce(
      (acc: number, t: AbsenceTimeLostType) => (t.dayType.id === 1 ? acc + 1 : acc),
      0,
    ) - 1
  );
};

export const getSelectedAbsence = (state: State): ?AbsenceType =>
  (state.recording || {}).selectedAbsence;

export const getAbsenceToUpdate = (state: State): ?AbsenceType => {
  const interactionType = selectInteractionType(state);

  if (interactionType === "REOPEN_ABSENCE") {
    return getSelectedAbsence(state);
  }

  return selectEmployeeOpenAbsence(state);
};

export const isEmployeeRecordSidebarExpanded = (state: State): boolean =>
  (state.recording || {}).isEmployeeRecordSidebarExpanded;

export const getEmployeeRecordNotifications = (
  state: State,
): Array<EmployeeRecordNotificationType> => (state.recording || {}).employeeRecordNotifications;

export const getEmployeeAbsenceInteractionsModal = (
  state: State,
): $Exact<{ visible: boolean, absenceId: ?number }> =>
  ((state.recording || {}).modals || {}).absenceInteractions;

export const getShowAddEmployeeModal = (state: State): boolean =>
  (((state.recording || {}).modals || {}).addEmployee || {}).visible;

export const getIsNewCreatedEmployee = (state: State): boolean =>
  (state.recording || {}).isNewCreatedEmployee;

export const getNurseTransferDecision = (state: State): ?string =>
  (state.recording || {}).nurseTransferDecision;

export const getNurseTransferViewModel = (state: State): NurseTransferViewModel =>
  (state.recording || {}).nurseTransfer;

export const getNurseTransferOffer = (state: Object) => {
  return (state.recording || {}).nurseTransferOffer;
};

export const getIncomingNurseTransferViewModel = (state: State): NurseTransferQueueViewModel =>
  (state.recording || {}).nurseTransferQueue;

export const getAcceptedNurseTransfer = (state: State): ?Object =>
  (state.recording || {}).acceptedTransfer;

export const getAcceptedNurseCallback = (state: State): ?Object =>
  (state.recording || {}).acceptedCallback;

export const getWillingToTriage = (state: State): boolean =>
  (state.recording || {}).willingToTriage;

export const selectEmployeeEapDetails = (state: State): Array<AbsenceType> =>
  selectEmployeeAbsenceRecording(state)?.eapDetails || [];

export const getAutoIdData: (state: State) => AutoIdDataType = state =>
  (state.recording || {}).autoIdData;

export const getCallPurpose: (state: State) => ?CallPurposeType = state =>
  (state.recording || {}).callPurpose;

export const getAutoIdDataPrefilled: (state: State) => boolean = state =>
  (state.recording || {}).autoIdDataPrefilled;

export const selectClientInformationData = (state: State): Array<*> => {
  return ((state.recording || {}).clientInformation || {}).list;
};

export const selectClientInformationSort = (state: State): any => {
  return ((((state.recording || {}).clientInformation || {}).pagination || {}).sort || {}).orders;
};

export const selectClientInformationPagination = (state: State): any => {
  return ((state.recording || {}).clientInformation || {}).pagination;
};

export const selectClientInformationTotalPages = (state: State): any => {
  return ((state.recording || {}).clientInformation || {}).totalElements;
};

export const selectAbsenceEmployeeScript = (key: any) => (state: State): any => {
  const isStartClosing =
    (state.recording || {}).selectedAbsence &&
    ((state.recording || {}).selectedAbsence || {}).isClosed &&
    (state.recording || {}).interactionType === "OPEN_ABSENCE";

  if (isStartClosing) return "";

  const script =
    (state.recording || {}).employeeScripts &&
    ((state.recording || {}).employeeScripts || []).find(script => script.keyName === key);
  return (script && script.scriptText) || "";
};

export const selectCallbackFormState = (state: State): ?Object => {
  return state.form && state.form["set-callback"]?.values;
};

export const selectMedicalTriageHistory = (state: State): MedicalTriageItem[] => {
  return state.recording.medicalTriageHistory.content;
};

export const selectTriageHistoryPagination = (state: State): PaginationExtendedType => {
  const pagination = state.recording.medicalTriageHistory.pagination;
  const totalElements = state.recording.medicalTriageHistory.totalElements;
  return {
    ...pagination,
    totalPages: Math.ceil(totalElements / config.resultsPerPage),
  };
};

export const selectMedicalTriageDetails = (state: State): TriageDetails | null => {
  return state.recording.medicalTriageDetails;
};

export const selectJobDuties = (state: State): JobDuty[] => {
  return state.recording.jobDuties;
};

export const getIsAbsenceClosed = (state: State): boolean => state.recording.isAbsenceClosed;
